import { useMemo, useState } from "react";
import type { ChangeEvent } from "react";
import {
  Alert,
  Box,
  Card,
  CardContent,
  SearchInput,
  Table,
  Typography
} from "@dealer-center/core-components";
import type { TypeSafeTableColumns } from "@dealer-center/core-components";
import type {
  NrtRecord,
  NrtRuleListResult
} from "@dealer-center/ddc-pages-nrt-database-filter-tool-interface";
import { renderOrPlaceholder } from "./nrtDate";
import { SR_ONLY_SX } from "./srOnlySx";
import { StatusChip } from "./StatusChip";

export interface RuleListPanelProps {
  result: NrtRuleListResult;
}

const HEADING_ID = "nrt-rule-list-heading";

const IDLE_MESSAGE = "Rule list would go here";
const LOADING_MESSAGE = "Loading rules for this account and provider…";
const EMPTY_MESSAGE = "No rules were found for this account and provider.";
const NO_SEARCH_MATCH_MESSAGE = "No rules match your search.";

// Smaller than RecordsTable's 10, since this panel sits inside a tall popup.
const RULE_LIST_PAGE_SIZE = 5;

// Same six fields the table shows.
const SEARCHABLE_FIELDS = [
  "createdBy",
  "status",
  "classification",
  "type",
  "select",
  "map"
] as const satisfies readonly (keyof NrtRecord)[];

const matchesRuleSearchTerm = (rule: NrtRecord, term: string) =>
  SEARCHABLE_FIELDS.some(field => {
    const value = rule[field];
    if (value === null || value === undefined) {
      return false;
    }
    return String(value).toLowerCase().includes(term);
  });

const RULE_LIST_COLUMNS: TypeSafeTableColumns<NrtRecord> = [
  { dataIndex: "createdBy", title: "Created By" },
  {
    dataIndex: "status",
    title: "Status",
    render: (status: NrtRecord["status"]) => <StatusChip status={status} />
  },
  {
    dataIndex: "classification",
    title: "Classification",
    render: renderOrPlaceholder
  },
  { dataIndex: "type", title: "Type", render: renderOrPlaceholder },
  { dataIndex: "select", title: "Select", render: renderOrPlaceholder },
  { dataIndex: "map", title: "Map", render: renderOrPlaceholder }
];

// Names every status, including 0 counts, rather than omitting it.
const buildHeading = (result: NrtRuleListResult): string => {
  if (result.status !== "SUCCESS" || !result.counts) return "Rules";
  const { total, active, inactive, testing } = result.counts;
  return `Total: ${total} rules — ${active} Active, ${inactive} Inactive, ${testing} Testing`;
};

export const RuleListPanel = ({ result }: RuleListPanelProps) => {
  const heading = buildHeading(result);
  const [searchValue, setSearchValue] = useState("");

  // Search narrows the rows only; the heading counts stay on the full fetch.
  const filteredRules = useMemo(() => {
    if (result.status !== "SUCCESS") {
      return [];
    }
    const term = searchValue.trim().toLowerCase();
    if (!term) {
      return result.rules;
    }
    return result.rules.filter(rule => matchesRuleSearchTerm(rule, term));
  }, [result, searchValue]);

  const handleSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSearchValue(event.target.value ?? "");
  };

  const isSearchVisible =
    result.status === "SUCCESS" && !!result.counts && result.counts.total > 0;

  return (
    <Box aria-labelledby={HEADING_ID} role="group">
      <Card
        content={
          <CardContent>
            <Box
              sx={{
                alignItems: "center",
                display: "flex",
                justifyContent: "space-between",
                marginBottom: 2
              }}
            >
              <Typography id={HEADING_ID} variant="h5">
                {heading}
              </Typography>
              {isSearchVisible && (
                <SearchInput
                  data-testid="nrt-rule-list-search-input"
                  inputValue={searchValue}
                  placeholder="Search rules..."
                  sx={{ maxWidth: "300px" }}
                  onClearCallback={() => setSearchValue("")}
                  onInputChange={handleSearchChange}
                />
              )}
            </Box>

            {/* Distinct wording from the visible Alert/heading below, so the
                live region never duplicates on-screen text verbatim. */}
            <Box aria-live="polite" role="status" sx={SR_ONLY_SX}>
              {result.status === "LOADING" && "Loading rule list."}
              {result.status === "ERROR" && "Rule list failed to load."}
              {result.status === "SUCCESS" &&
                result.counts &&
                `Rule list loaded: ${result.counts.total} rules.`}
            </Box>

            {result.status === "IDLE" && (
              <Typography
                sx={{ color: "#757575", fontStyle: "italic" }}
                variant="body-md"
              >
                {IDLE_MESSAGE}
              </Typography>
            )}

            {result.status === "LOADING" && (
              <Typography variant="body-md">{LOADING_MESSAGE}</Typography>
            )}

            {result.status === "ERROR" && (
              <Alert role="alert" type="error">
                {result.message ?? "Failed to load rules."}
              </Alert>
            )}

            {result.status === "SUCCESS" &&
              result.counts &&
              (result.counts.total === 0 ? (
                <Typography variant="body-md">{EMPTY_MESSAGE}</Typography>
              ) : (
                <Table.Table
                  columns={RULE_LIST_COLUMNS}
                  currentPageSize={RULE_LIST_PAGE_SIZE}
                  data={filteredRules}
                  dataDensity="small"
                  emptyText={NO_SEARCH_MATCH_MESSAGE}
                  rowKey="id"
                />
              ))}
          </CardContent>
        }
      />
    </Box>
  );
};
